/**
 * FilterNodeInput Component
 * Handles individual field filter input (field selector, operator, value)
 */

import { useMemo, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import type { Field } from '@/hooks/useFields';
import type { ExtendedField } from '@/hooks/useRelationalFields';
import type { Filter } from './FilterNodes';
import {
    getFilterOperatorsForType,
    getDefaultOperator,
    operatorLabels,
    operatorNeedsValue,
    operatorExpectsArray,
    type FilterOperator,
} from '@/utils/filter-operators';

interface FilterNodeInputProps {
    filter: Filter;
    fields: Field[];
    onChange: (filter: Filter) => void;
    disabled?: boolean;
}

/**
 * Get the field path from a filter node
 */
function getField(node: Record<string, unknown>): string {
    const keys = Object.keys(node);
    if (keys.length === 0) return '';

    const name = keys[0];
    if (!name || name.startsWith('_')) return '';

    const value = node[name];
    if (!value || typeof value !== 'object') return '';

    const subFields = getField(value as Record<string, unknown>);
    return subFields !== '' ? `${name}.${subFields}` : name;
}

/**
 * Get the operator from a filter node
 */
function getOperator(node: Record<string, unknown>): string {
    const fieldPath = getField(node);
    if (!fieldPath) return '_eq';

    const parts = fieldPath.split('.');
    let current: unknown = node;

    for (const part of parts) {
        if (current && typeof current === 'object') {
            current = (current as Record<string, unknown>)[part];
        } else {
            return '_eq';
        }
    }

    if (current && typeof current === 'object') {
        return Object.keys(current)[0] || '_eq';
    }
    return '_eq';
}

/**
 * Get the value from a filter node
 */
function getValue(node: Record<string, unknown>): unknown {
    const fieldPath = getField(node);
    const operator = getOperator(node);
    if (!fieldPath || !operator) return null;

    const parts = fieldPath.split('.');
    let current: unknown = node;

    for (const part of parts) {
        if (current && typeof current === 'object') {
            current = (current as Record<string, unknown>)[part];
        } else {
            return null;
        }
    }

    if (current && typeof current === 'object') {
        return (current as Record<string, unknown>)[operator];
    }
    return null;
}

/**
 * Build a filter from field, operator, and value
 */
function buildFilter(field: string, operator: string, value: unknown): Filter {
    const sections = field.split('.');

    function buildNested(parts: string[]): Record<string, unknown> {
        const part = parts.shift();
        if (part !== undefined) {
            return { [part]: buildNested(parts) };
        } else {
            return { [operator]: value };
        }
    }

    return buildNested(sections);
}

export function FilterNodeInput({
    filter,
    fields,
    onChange,
    disabled = false,
}: FilterNodeInputProps) {
    // Extract current values
    const fieldPath = useMemo(() => getField(filter), [filter]);
    const operator = useMemo(() => getOperator(filter), [filter]);
    const value = useMemo(() => getValue(filter), [filter]);

    // Get selected field info
    const selectedField = useMemo(() => {
        return fields.find((f) => f.field === fieldPath);
    }, [fields, fieldPath]);

    // Get available operators for selected field type
    const availableOperators = useMemo(() => {
        const fieldType = selectedField?.type || 'string';
        return getFilterOperatorsForType(fieldType);
    }, [selectedField]);

    // Current operator without underscore
    const currentOperator = useMemo(() => {
        return operator.replace('_', '') as FilterOperator;
    }, [operator]);

    // Check if value input is needed
    const needsValue = useMemo(() => {
        return operatorNeedsValue(currentOperator);
    }, [currentOperator]);

    // Check if operator expects array
    const expectsArray = useMemo(() => {
        return operatorExpectsArray(currentOperator);
    }, [currentOperator]);

    // Get input type based on field type
    const inputType = useMemo(() => {
        const fieldType = selectedField?.type?.toLowerCase() || 'string';

        switch (fieldType) {
            case 'integer':
            case 'biginteger':
            case 'float':
            case 'decimal':
                return 'number';
            case 'date':
                return 'date';
            case 'datetime':
            case 'timestamp':
                return 'datetime-local';
            case 'time':
                return 'time';
            default:
                return 'text';
        }
    }, [selectedField]);

    // Handle field change
    const handleFieldChange = useCallback((newFieldPath: string) => {
        const newField = fields.find((f) => f.field === newFieldPath);
        const newFieldType = newField?.type || 'string';
        const newOperators = getFilterOperatorsForType(newFieldType);

        // Check if current operator is valid for new field type
        const currentOp = operator.replace('_', '') as FilterOperator;
        const newOperator = newOperators.includes(currentOp)
            ? operator
            : `_${getDefaultOperator(newFieldType)}`;

        onChange(buildFilter(newFieldPath, newOperator, null));
    }, [fields, operator, onChange]);

    // Handle operator change
    const handleOperatorChange = useCallback((newOp: string) => {
        const newOperator = `_${newOp}`;

        // Determine new value based on operator
        let newValue: unknown = value;

        if (!operatorNeedsValue(newOp as FilterOperator)) {
            newValue = true;
        } else if (operatorExpectsArray(newOp as FilterOperator)) {
            newValue = Array.isArray(value) ? value : [value].filter(Boolean);
        } else {
            newValue = Array.isArray(value) ? value[0] : value;
        }

        onChange(buildFilter(fieldPath, newOperator, newValue));
    }, [fieldPath, value, onChange]);

    // Handle value change
    const handleValueChange = useCallback((newValue: unknown) => {
        onChange(buildFilter(fieldPath, operator, newValue));
    }, [fieldPath, operator, onChange]);

    // Render value input
    const renderValueInput = () => {
        if (!needsValue) {
            return null;
        }

        const fieldType = selectedField?.type?.toLowerCase() || 'string';

        // Boolean field with eq/neq operators
        if (fieldType === 'boolean' && ['eq', 'neq'].includes(currentOperator)) {
            return (
                <Select
                    value={value === true ? 'true' : value === false ? 'false' : ''}
                    onValueChange={(v) => handleValueChange(v === 'true')}
                    disabled={disabled}
                >
                    <SelectTrigger className="w-24 h-8">
                        <SelectValue placeholder="Value" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="true">True</SelectItem>
                        <SelectItem value="false">False</SelectItem>
                    </SelectContent>
                </Select>
            );
        }

        // Field has choices/options
        if (selectedField?.meta?.options?.choices) {
            const choices = selectedField.meta.options.choices as Array<{ text: string; value: string }>;

            if (expectsArray) {
                return (
                    <div className="flex flex-wrap gap-1 min-w-[150px]">
                        {choices.map((choice) => {
                            const values = Array.isArray(value) ? value : [];
                            const isSelected = values.includes(choice.value);

                            return (
                                <Button
                                    key={choice.value}
                                    variant={isSelected ? 'default' : 'outline'}
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => {
                                        const newValues = isSelected
                                            ? values.filter((v: unknown) => v !== choice.value)
                                            : [...values, choice.value];
                                        handleValueChange(newValues);
                                    }}
                                    disabled={disabled}
                                >
                                    {choice.text}
                                </Button>
                            );
                        })}
                    </div>
                );
            }

            return (
                <Select
                    value={String(value || '')}
                    onValueChange={handleValueChange}
                    disabled={disabled}
                >
                    <SelectTrigger className="w-36 h-8">
                        <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                        {choices.map((choice) => (
                            <SelectItem key={choice.value} value={choice.value}>
                                {choice.text}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );
        }

        // Between operator - two inputs
        if (['between', 'nbetween'].includes(currentOperator)) {
            const values = Array.isArray(value) ? value : [null, null];

            return (
                <div className="flex items-center gap-1">
                    <Input
                        type={inputType}
                        className="w-24 h-8"
                        placeholder="From"
                        value={values[0] ?? ''}
                        onChange={(e) => handleValueChange([e.target.value || null, values[1]])}
                        disabled={disabled}
                    />
                    <span className="text-xs text-muted-foreground">and</span>
                    <Input
                        type={inputType}
                        className="w-24 h-8"
                        placeholder="To"
                        value={values[1] ?? ''}
                        onChange={(e) => handleValueChange([values[0], e.target.value || null])}
                        disabled={disabled}
                    />
                </div>
            );
        }

        // In/nin operators - comma-separated values
        if (['in', 'nin'].includes(currentOperator)) {
            const values = Array.isArray(value) ? value : [];

            return (
                <Input
                    type="text"
                    className="w-48 h-8"
                    placeholder="value1, value2, ..."
                    value={values.join(', ')}
                    onChange={(e) => {
                        const newValues = e.target.value
                            .split(',')
                            .map((v) => v.trim())
                            .filter(Boolean);
                        handleValueChange(newValues);
                    }}
                    disabled={disabled}
                />
            );
        }

        // Default input
        const displayValue = typeof value === 'string' || typeof value === 'number'
            ? value
            : '';

        return (
            <Input
                type={inputType}
                className="w-36 h-8"
                placeholder="Value"
                value={displayValue}
                onChange={(e) => {
                    const newValue = inputType === 'number'
                        ? (e.target.value === '' ? null : Number(e.target.value))
                        : e.target.value;
                    handleValueChange(newValue);
                }}
                disabled={disabled}
            />
        );
    };

    return (
        <div className="flex items-center gap-2 p-2 rounded-md border border-border bg-background">
            {/* Field selector */}
            <Select
                value={fieldPath}
                onValueChange={handleFieldChange}
                disabled={disabled}
            >
                <SelectTrigger className="w-44 h-8">
                    <SelectValue placeholder="Field" />
                </SelectTrigger>
                <SelectContent>
                    {(() => {
                        // Separate base fields from relational sub-fields
                        const baseFields = fields.filter((f) => !(f as ExtendedField)._isRelated);
                        const relatedFields = fields.filter((f) => (f as ExtendedField)._isRelated);

                        // Group related fields by relation field label
                        const relationGroups = new Map<string, { label: string; fields: Field[] }>();
                        for (const f of relatedFields) {
                            const ef = f as ExtendedField;
                            const key = ef._relationField ?? '';
                            if (!relationGroups.has(key)) {
                                relationGroups.set(key, {
                                    label: ef._relationLabel ?? key,
                                    fields: [],
                                });
                            }
                            relationGroups.get(key)!.fields.push(f);
                        }

                        return (
                            <>
                                {/* Current collection fields */}
                                <SelectGroup>
                                    <SelectLabel className="text-xs text-muted-foreground">
                                        Fields
                                    </SelectLabel>
                                    {baseFields.map((field) => (
                                        <SelectItem key={field.field} value={field.field}>
                                            {field.name || field.field}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>

                                {/* Related collection fields, one group per relation */}
                                {Array.from(relationGroups.entries()).map(([key, group]) => (
                                    <SelectGroup key={key}>
                                        <SelectSeparator />
                                        <SelectLabel className="text-xs text-muted-foreground">
                                            {group.label}
                                        </SelectLabel>
                                        {group.fields.map((field) => (
                                            <SelectItem key={field.field} value={field.field}>
                                                {field.name || field.field}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                ))}
                            </>
                        );
                    })()}
                </SelectContent>
            </Select>

            {/* Operator selector */}
            <Select
                value={currentOperator}
                onValueChange={handleOperatorChange}
                disabled={disabled}
            >
                <SelectTrigger className="w-40 h-8">
                    <SelectValue placeholder="Operator" />
                </SelectTrigger>
                <SelectContent>
                    {availableOperators.map((op) => (
                        <SelectItem key={op} value={op}>
                            {operatorLabels[op]}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {/* Value input */}
            {renderValueInput()}
        </div>
    );
}
